import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Circle, Loader2, PhoneOff, Video } from "lucide-react";
import { ICE, VIDEO_360, iceQueue, startPolling } from "./rtc";
import { createRecorder } from "./recorder";
import { IdentitaPanel } from "./IdentitaPanel";

const STATO_CONN = { new: "In attesa del cliente", connecting: "Collegamento…", connected: "Collegati", disconnected: "Connessione instabile", failed: "Connessione non riuscita", closed: "Chiusa" };

function useCall(sid, localStream, remoteRef, setConn, setSess) {
  const pcRef = useRef(null);
  const remoteStream = useRef(null);
  useEffect(() => {
    if (!localStream) return undefined;
    let cid = null, q = null;
    const send = (tipo, dati) => api.post(`/trattative/videochiamate/${sid}/segnali`, { tipo, dati }).catch(() => {});
    const nuovaChiamata = async (nuovoCid) => {
      pcRef.current?.close();
      cid = nuovoCid;
      const pc = new RTCPeerConnection(ICE);
      pcRef.current = pc; q = iceQueue(pc);
      localStream.getTracks().forEach((tr) => pc.addTrack(tr, localStream));
      pc.ontrack = (e) => { remoteStream.current = e.streams[0]; if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; };
      pc.onicecandidate = (e) => { if (e.candidate) send("ice", { cid, c: e.candidate.toJSON() }); };
      pc.onconnectionstatechange = () => setConn(pc.connectionState);
      await pc.setLocalDescription(await pc.createOffer());
      send("offer", { cid, sdp: pc.localDescription.sdp });
      setConn("connecting");
    };
    const stop = startPolling((n) => api.get(`/trattative/videochiamate/${sid}/segnali?dopo=${n}`), async (batch, data) => {
      setSess(data.sessione);
      const last = batch.map((x) => x.tipo).lastIndexOf("pronto");
      for (const m of last >= 0 ? batch.slice(last) : batch) {
        const d = m.dati || {};
        if (m.tipo === "pronto") await nuovaChiamata(d.cid);
        else if (d.cid !== cid || !pcRef.current) continue;
        else if (m.tipo === "answer") { await pcRef.current.setRemoteDescription({ type: "answer", sdp: d.sdp }); await q.flush(); }
        else if (m.tipo === "ice") await q.add(d.c);
      }
    });
    return () => { stop(); pcRef.current?.close(); };
  }, [sid, localStream, remoteRef, setConn, setSess]);
  return remoteStream;
}

function useRecording(sid, refs) {
  const rec = useRef(null);
  const queue = useRef(Promise.resolve());
  const [saved, setSaved] = useState(0);
  const [start, setStart] = useState(null);
  const upload = (seq, blob) => {
    queue.current = queue.current.then(async () => {
      for (let i = 0; i < 4; i++) {
        try { const fd = new FormData(); fd.append("seq", seq); fd.append("file", blob, `p${seq}.webm`); await api.post(`/trattative/videochiamate/${sid}/registrazione/parte`, fd); setSaved((x) => x + 1); return; }
        catch { await new Promise((r) => setTimeout(r, 2000 * (i + 1))); }
      }
      toast.error(`Parte ${seq} della registrazione non salvata`);
    });
  };
  const avvia = (startSeq) => { rec.current = createRecorder({ ...refs(), onChunk: upload, startSeq }); setStart(Date.now()); };
  const ferma = async () => { if (rec.current) { await rec.current.stop(); rec.current = null; } await queue.current; return start ? Math.round((Date.now() - start) / 1000) : 0; };
  return { avvia, ferma, saved, start };
}

export default function VideoOperatore() {
  const { id, sid } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [s, setSess] = useState(null);
  const [conn, setConn] = useState("new");
  const [local, setLocal] = useState(null);
  const [busy, setBusy] = useState(false);
  const localRef = useRef(null), remoteRef = useRef(null);
  useEffect(() => { api.get(`/trattative/${id}`).then(({ data }) => setT(data)); }, [id]);
  useEffect(() => {
    let st, annullato = false;
    navigator.mediaDevices.getUserMedia({ video: VIDEO_360, audio: true }).then((m) => {
      if (annullato) { m.getTracks().forEach((x) => x.stop()); return; }
      st = m; setLocal(m);
    }).catch(() => toast.error("Consenta l'accesso a fotocamera e microfono"));
    return () => { annullato = true; st?.getTracks().forEach((x) => x.stop()); };
  }, []);
  // l'anteprima locale viene montata solo dopo il caricamento di trattativa e sessione: collega lo stream quando il <video> esiste
  useEffect(() => { if (local && localRef.current && localRef.current.srcObject !== local) localRef.current.srcObject = local; });
  const remoteStream = useCall(sid, local, remoteRef, setConn, useCallback((v) => setSess(v), []));
  const recording = useRecording(sid, () => ({ remoteVideo: remoteRef.current, localVideo: localRef.current, remoteStream: remoteStream.current, localStream: local }));
  const avviaRec = async () => {
    try { const { data } = await api.post(`/trattative/videochiamate/${sid}/registrazione/avvia`); recording.avvia(data.rec?.n_parti || 0); toast.success("Registrazione avviata: ripeta l'avviso a voce"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const termina = async () => {
    const idt = s?.identita || {};
    if (t?.canale === "distanza" && !idt.approvata_at) {
      const manca = [!(s?.catture?.volto && s?.catture?.doc_fronte && s?.catture?.doc_retro) && "le 3 acquisizioni (volto, documento fronte e retro)", !idt.dati && "i dati del documento",
        !idt.confermata_cliente_at && "la conferma con OTP del cliente (sul suo telefono)", "la sua approvazione con «Conferma identificazione»"].filter(Boolean);
      if (!window.confirm(`ATTENZIONE: l'identificazione non è completata. Mancano: ${manca.join("; ")}.\n\nSe chiude ora, per proseguire con la vendita a distanza servirà una nuova videochiamata.\n\nTerminare comunque?`)) return;
    } else if (!window.confirm("Terminare la videochiamata?")) return;
    setBusy(true);
    try { const durata = await recording.ferma(); await api.post(`/trattative/videochiamate/${sid}/termina`, { durata_s: durata }); toast.success("Videochiamata conclusa"); nav(`/trattative/${id}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!t || !s) return <div className="p-10 text-slate-400">Caricamento…</div>;
  const inRec = s.stato === "registrazione" && recording.start;
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-5 py-5">
        <button onClick={() => nav(`/trattative/${id}`)} className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-3" data-testid="vid-back"><ArrowLeft className="h-4 w-4" /> {t.codice} · {t.cliente.nome}</button>
        <div className="grid lg:grid-cols-[1fr_360px] gap-4">
          <div className="space-y-3">
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video" data-testid="vid-stage">
              <video ref={remoteRef} autoPlay playsInline className="w-full h-full object-contain" data-testid="vid-remote" />
              <video ref={localRef} autoPlay playsInline muted className="absolute bottom-3 right-3 w-40 rounded-lg border border-white/20 bg-slate-800" data-testid="vid-local" />
              {inRec && <div className="absolute top-3 left-3 bg-red-600 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5" data-testid="vid-rec-badge"><Circle className="h-2.5 w-2.5 fill-white animate-pulse" /> REC · {recording.saved} parti salvate</div>}
              <div className="absolute top-3 right-3 bg-black/60 text-xs px-2.5 py-1 rounded-full" data-testid="vid-conn">{STATO_CONN[conn] || conn}</div>
            </div>
            {conn === "failed" && <div className="rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm p-3" data-testid="vid-failed">La rete del cliente non permette il collegamento diretto. Riprogrammi la videochiamata o concluda la vendita in presenza (mai solo per telefono).</div>}
            <div className="flex gap-2 flex-wrap">
              {!inRec && s.stato !== "conclusa" && <Button onClick={avviaRec} disabled={conn !== "connected" || !s.consenso?.at} variant="destructive" data-testid="vid-avvia-rec"><Video className="h-4 w-4 mr-2" /> Avvia registrazione</Button>}
              <Button variant="outline" onClick={termina} disabled={busy} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="vid-termina">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneOff className="h-4 w-4 mr-2" />} Termina videochiamata</Button>
            </div>
            <p className="text-xs text-slate-400" data-testid="vid-consenso">{s.consenso?.at ? `Consenso alla registrazione dato dal cliente alle ${new Date(s.consenso.at).toLocaleTimeString("it-IT")}.` : "In attesa che il cliente apra il link e dia il consenso alla registrazione."} Tenga questa scheda in primo piano durante la registrazione.</p>
          </div>
          <div className="bg-white text-slate-900 rounded-xl p-4">
            {inRec || s.identita?.approvata_at ? <IdentitaPanel s={s} t={t} remoteRef={remoteRef} onSess={setSess} />
              : <p className="text-sm text-slate-500" data-testid="vid-id-attesa">Il riconoscimento si attiva quando la registrazione è in corso.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
